import { Button, Form, Pagination, Space, Table, Tooltip, Tag } from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  HistoryOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import UserAvatar from 'components/user-avatar';
import { ColumnsType } from 'antd/lib/table';
import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import PageTitle from '../../components/page-title';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import Filter, { AttendanceFilterFormValue } from './component/filter';
import ExportAllUsersModal from './component/export-all-modal';
import ExportTimesheetModal from 'components/export-timesheet-modal';
import CreateAttendanceModal from './component/create-attendance-modal';
import EditAttendanceModal from './component/edit-attendance-modal';
import AttendanceHistoryDrawer from './component/attendance-history-drawer';
import AttendancePhotos from './component/attendance-photos';
import {
  attendanceAvatarUrl,
  attendanceFullName,
  attendancePersonName,
} from './helpers/attendance-person';
import { colors } from 'theme/tokens';
import moment from 'moment';
import { formatData } from 'utils/common';
import { ATTENDANCE_STATUS } from 'constants/common';
import { Attendance } from 'types/attendance';
import {
  LinkedFilterTag,
  useDropSearchParams,
  useParamChange,
} from 'pages/user-manager/helpers/user-deep-link';

const LIMIT = 10;

const initFilter = {
  page: 1,
  limit: LIMIT,
  month: moment().format('YYYY-MM'),
  date: moment().format('YYYY-MM-DD'),
};

export interface AttendanceFilter {
  limit: number;
  page: number;
  month?: string;
  date?: string;
  username?: string;
  email?: string;
  status?: ATTENDANCE_STATUS;
}

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const DATE_RE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const STATUS_VALUES = Object.values(ATTENDANCE_STATUS) as string[];

/**
 * Liên kết sâu (API /attendance/list không nhận userId):
 *   ?username=<TikTok ID> | ?email=<email>   lọc một người (so khớp gần đúng như ô tìm kiếm)
 *   ?month=YYYY-MM  ?date=YYYY-MM-DD  ?status=normal|late|early|late-early|absent|no-checkout
 * Lọc theo người/tháng mà không kèm date → cả tháng; chỉ có status → vẫn là hôm nay.
 */
function readDeepLink(params: URLSearchParams) {
  const username = params.get('username')?.trim() || undefined;
  const email = params.get('email')?.trim() || undefined;
  const rawStatus = params.get('status') || '';
  const status = STATUS_VALUES.includes(rawStatus) ? (rawStatus as ATTENDANCE_STATUS) : undefined;
  const rawDate = params.get('date') || '';
  const rawMonth = params.get('month') || '';
  const dateParam = DATE_RE.test(rawDate) ? rawDate : undefined;
  const monthParam = MONTH_RE.test(rawMonth) ? rawMonth : undefined;
  if (!username && !email && !status && !dateParam && !monthParam) return null;

  const wholeMonth = !dateParam && !!(username || email || monthParam);
  const date = dateParam || (wholeMonth ? undefined : initFilter.date);
  const month = date ? date.slice(0, 7) : monthParam || initFilter.month;
  const filter: AttendanceFilter = formatData({
    page: 1,
    limit: LIMIT,
    month,
    date,
    username,
    email,
    status,
  });
  const preset: Partial<AttendanceFilterFormValue> = {
    username: username || '',
    email: email || '',
    month: dayjs(`${month}-01`),
    date: date ? dayjs(date) : null,
    status,
  };
  return { filter, preset };
}

// Lề ngang ô 12px (antd mặc định 16px): bảng 8 cột vừa khung ở màn 1280px kể cả khi trình duyệt
// có thanh cuộn dọc thật (khung nội dung chỉ còn ~952px).
const CELL_PAD_X = 12;
const noWrapHeader = () => ({
  style: { whiteSpace: 'nowrap' as const, paddingInline: CELL_PAD_X },
});
const compactCell = () => ({ style: { paddingInline: CELL_PAD_X } });

/** Giờ chấm công gọn (HH:mm:ss); nếu rơi sang ngày khác ngày công (ca qua đêm) thì ghi thêm ngày. */
function renderClock(value: string | null | undefined, record: Attendance) {
  if (!value) return <span style={{ color: colors.textMuted }}>—</span>;
  const m = moment(value);
  const otherDay = !!record?.date && m.format('YYYY-MM-DD') !== String(record.date).slice(0, 10);
  return (
    <div style={{ whiteSpace: 'nowrap', lineHeight: 1.35 }}>
      {/* Giờ:phút là đủ để đọc; giây đầy đủ vẫn xem được khi rê chuột. */}
      <div title={m.format('HH:mm:ss DD/MM/YYYY')}>{m.format('HH:mm')}</div>
      {otherDay && (
        <div style={{ fontSize: 12, color: colors.textSecondary }}>ngày {m.format('DD/MM')}</div>
      )}
    </div>
  );
}

const AttendanceManager = () => {
  const [loading, setLoading] = useState(true);
  const [dataResponse, setDataResponse] = useState<any>({
    items: [],
    totalCount: 0,
  });
  const [searchParams] = useSearchParams();
  const dropSearchParams = useDropSearchParams();
  const [deepLink] = useState(() => readDeepLink(searchParams));
  const [filterForm] = Form.useForm<AttendanceFilterFormValue>();
  const linkedUsername = searchParams.get('username')?.trim() || undefined;
  const linkedEmail = searchParams.get('email')?.trim() || undefined;
  const [filter, setFilter] = useState<AttendanceFilter>(deepLink?.filter || initFilter);
  const [timesheetOpen, setTimesheetOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Attendance | null>(null);
  const [historyTarget, setHistoryTarget] = useState<Attendance | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  const onFinish = (value: Partial<AttendanceFilter>) => {
    setFilter((preState) => formatData({ ...preState, ...value, page: 1 }));
    // Đổi người / Xoá lọc → bỏ ?username= cũ khỏi URL để F5 không lọc lại người cũ.
    if (
      (linkedUsername && value.username !== linkedUsername) ||
      (linkedEmail && value.email !== linkedEmail)
    ) {
      dropSearchParams('username', 'email');
    }
  };

  // URL đổi sau khi trang đã mở (đóng thẻ "Đang lọc", bấm lại menu, Back/Forward).
  const syncLinkedField = (field: 'username' | 'email', next?: string, prev?: string) => {
    setFilter((p) => {
      if (next) return { ...p, [field]: next, page: 1 };
      return p[field] === prev ? formatData({ ...p, [field]: undefined, page: 1 }) : p;
    });
    if (next) filterForm.setFieldValue(field, next);
    else if (filterForm.getFieldValue(field) === prev) filterForm.setFieldValue(field, '');
  };
  useParamChange(linkedUsername, (next, prev) => syncLinkedField('username', next, prev));
  useParamChange(linkedEmail, (next, prev) => syncLinkedField('email', next, prev));
  const linkedLabel =
    (linkedUsername && filter.username === linkedUsername && linkedUsername) ||
    (linkedEmail && filter.email === linkedEmail && linkedEmail) ||
    undefined;

  useEffect(() => {
    const fetchAttendance = async () => {
      setLoading(true);
      try {
        const res: any = await AxiosUtils.get(API.attendanceList, {
          ...filter,
        });
        const items = res?.data?.data || res?.data?.items || res?.data || [];
        const total = res?.data?.metadata?.total || res?.data?.totalCount || 0;
        setDataResponse({ items, totalCount: total });
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [filter, refreshKey]);

  const baseColumns: ColumnsType<any> = [
    {
      title: 'Nhân viên',
      key: 'user',
      onHeaderCell: noWrapHeader,
      render: (record: Attendance) => {
        const username = record?.user?.username ?? '';
        const fullName = attendanceFullName(record?.user);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAvatar
              shape="square"
              src={attendanceAvatarUrl(record?.user)}
              size={32}
              style={{ flex: 'none', borderRadius: 8 }}
            />
            <div style={{ lineHeight: 1.35, whiteSpace: 'nowrap' }}>
              <div style={{ fontWeight: 500, color: colors.textPrimary }}>
                {fullName || username}
              </div>
              {fullName && username && (
                <Tooltip title="TikTok ID">
                  <div style={{ fontSize: 12, color: colors.textSecondary }}>{username}</div>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Ảnh chấm công',
      key: 'photos',
      onHeaderCell: noWrapHeader,
      render: (record: Attendance) => (
        <AttendancePhotos record={record} personName={attendancePersonName(record?.user)} />
      ),
    },
    {
      title: 'Ngày',
      dataIndex: 'date',
      key: 'date',
      onHeaderCell: noWrapHeader,
      render: (value) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          {value ? moment(value).format('DD/MM/YYYY') : ''}
        </span>
      ),
    },
    {
      title: 'Giờ vào',
      dataIndex: 'checkinTime',
      key: 'checkinTime',
      onHeaderCell: noWrapHeader,
      render: (value, record: Attendance) => renderClock(value, record),
    },
    {
      title: 'Giờ ra',
      dataIndex: 'checkoutTime',
      key: 'checkoutTime',
      onHeaderCell: noWrapHeader,
      render: (value, record: Attendance) => renderClock(value, record),
    },
    {
      title: 'Ca làm',
      key: 'shift',
      onHeaderCell: noWrapHeader,
      // Chỉ xuống dòng giữa các từ: khung giờ "09:00-18:00" không bị tách đôi ở dấu gạch nối.
      render: (record) => (
        <span>
          {String(record?.shift?.name ?? '')
            .split(/\s+/)
            .filter(Boolean)
            .map((word, i) => (
              <Fragment key={i}>
                {i > 0 && ' '}
                <span style={{ whiteSpace: 'nowrap' }}>{word}</span>
              </Fragment>
            ))}
        </span>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 136,
      onHeaderCell: noWrapHeader,
      render: (value: ATTENDANCE_STATUS) => {
        const metaMap: Record<ATTENDANCE_STATUS, { text: string; color: string }> = {
          [ATTENDANCE_STATUS.NORMAL]: { text: 'Đủ công', color: 'green' },
          [ATTENDANCE_STATUS.LATE]: { text: 'Checkin muộn', color: 'volcano' },
          [ATTENDANCE_STATUS.EARLY]: { text: 'Checkout sớm', color: 'gold' },
          [ATTENDANCE_STATUS.LATE_EARLY]: { text: 'Thiếu checkin và checkout', color: 'red' },
          [ATTENDANCE_STATUS.ABSENT]: { text: 'Chưa có thông tin chấm công', color: 'default' },
          [ATTENDANCE_STATUS.NO_CHECKOUT]: { text: 'Chưa checkout', color: 'geekblue' },
          [ATTENDANCE_STATUS.FORGOT_CHECKIN]: { text: 'Quên Checkin', color: 'orange' },
        };
        const meta = metaMap[value as ATTENDANCE_STATUS];
        return meta ? (
          <Tag
            color={meta.color}
            // Nhãn dài ("Thiếu checkin và checkout") được xuống dòng thay vì làm bảng tràn ngang.
            style={{
              borderRadius: 6,
              padding: '2px 6px',
              marginInlineEnd: 0,
              whiteSpace: 'normal',
              lineHeight: '18px',
            }}
          >
            {meta.text}
          </Tag>
        ) : (
          <span />
        );
      },
    },
    {
      title: 'Thao tác',
      key: 'actions',
      fixed: 'right' as const,
      width: 96,
      onHeaderCell: noWrapHeader,
      render: (_: any, record: Attendance) => (
        <Space size={4}>
          <Tooltip title="Sửa">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              aria-label="Sửa"
              onClick={() => setEditTarget(record)}
            />
          </Tooltip>
          <Tooltip title="Lịch sử">
            <Button
              type="text"
              size="small"
              icon={<HistoryOutlined />}
              aria-label="Lịch sử"
              onClick={() => setHistoryTarget(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  const columns: ColumnsType<any> = baseColumns.map((col) => ({ onCell: compactCell, ...col }));
  const hasRows = Array.isArray(dataResponse.items) && dataResponse.items.length > 0;

  const handleChangePage = (page: number) => {
    setFilter((preState: any) => ({ ...preState, page }));
  };

  return (
    <div>
      <PageTitle
        title="Chấm công"
        extra={
          <Space wrap>
            <CustomButton
              bgColor="third-green"
              text="Xuất bản ghi (Excel)"
              icon={<DownloadOutlined />}
              onClick={() => setExportOpen(true)}
            />
            <CustomButton
              bgColor="third-green"
              text="Xuất bảng công & lương"
              icon={<DownloadOutlined />}
              onClick={() => setTimesheetOpen(true)}
            />
            <CustomButton
              bgColor="primary-green"
              text="Tạo chấm công"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            />
          </Space>
        }
      />
      <div style={{ marginBottom: 24 }}>
        <Filter form={filterForm} preset={deepLink?.preset} onSubmit={onFinish} />
      </div>
      {linkedLabel && (
        <LinkedFilterTag
          label={linkedLabel}
          onClose={() => dropSearchParams('username', 'email')}
        />
      )}
      <div className="custom-table custom-form">
        <Table
          columns={columns}
          rowKey={(record) => record.id || `${record.username}-${record.date}`}
          pagination={false}
          dataSource={dataResponse.items}
          // Tự co cột (chữ ca làm/trạng thái được xuống dòng); hẹp hơn 940px thì cuộn ngang trong bảng.
          // Bảng trống thì bỏ scroll.x: antd cho dòng "Trống" một độ rộng cố định lớn hơn khung
          // 2px → hiện vạch bóng cột "Thao tác" và cắt góc thẻ bảng.
          tableLayout="auto"
          scroll={hasRows ? { x: 940 } : undefined}
          loading={loading}
        />
        {dataResponse.totalCount > LIMIT && (
          <Pagination
            showSizeChanger={false}
            defaultCurrent={LIMIT}
            current={filter.page}
            onChange={handleChangePage}
            showLessItems
            total={dataResponse.totalCount}
            pageSize={LIMIT}
          />
        )}
      </div>
      <ExportAllUsersModal open={exportOpen} onClose={() => setExportOpen(false)} />
      <ExportTimesheetModal open={timesheetOpen} onClose={() => setTimesheetOpen(false)} />
      <CreateAttendanceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={triggerRefresh}
      />
      <EditAttendanceModal
        open={!!editTarget}
        attendance={editTarget}
        onClose={() => setEditTarget(null)}
        onSuccess={triggerRefresh}
      />
      <AttendanceHistoryDrawer
        open={historyTarget !== null}
        attendanceId={historyTarget?.id ?? null}
        attendance={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </div>
  );
};

export default AttendanceManager;
